import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { Connection } from '../obj/Connection';
import { Flow, normalizeSources } from '../obj/Flow';
import { FlowRun } from '../obj/FlowRun';
import { MsalHelperService } from './msal-helper.service';
import { RunScanQueueOptions, StorageBackend, StorageMode, StoragePutOptions, EnvScanSource, StoredEnvironment, StoreStats } from './storage-backend';
import { ConfigurationItem, UserItem } from './storage-types';
import { StorageWorkerClientService } from './storage-worker-client.service';

@Injectable({
  providedIn: 'root',
})
export class StorageWorkerBackendService implements StorageBackend {
  private initPromise: Promise<void> | null = null;
  private flowsSubject = new BehaviorSubject<Flow[]>([]);
  private flowsAdminSubject = new BehaviorSubject<Flow[]>([]);
  private runsSubject = new BehaviorSubject<FlowRun[]>([]);
  private usersSubject = new BehaviorSubject<UserItem[]>([]);
  private connectionsSubject = new BehaviorSubject<Connection[]>([]);
  private flowRefreshScheduled = false;
  private runsRefreshScheduled = false;
  private usersRefreshScheduled = false;
  private connectionsRefreshScheduled = false;

  constructor(
    private worker: StorageWorkerClientService,
    private msalHelperService: MsalHelperService,
  ) {}

  getMode(): StorageMode {
    return 'worker-opfs';
  }

  async ensureReady(): Promise<void> {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.worker.init({
      userOid: this.msalHelperService.getUser()?.['oid'] || '',
    }).then(() => undefined);
    return this.initPromise;
  }

  async flush(): Promise<void> {
    await this.ensureReady();
    await this.worker.flush();
  }

  async getConfig(key: string): Promise<ConfigurationItem> {
    await this.ensureReady();
    return this.worker.getConfig(key);
  }

  async putConfig(key: string, item: ConfigurationItem): Promise<any> {
    await this.ensureReady();
    return this.worker.putConfig(key, item);
  }

  async getFlows(): Promise<Flow[]> {
    await this.ensureReady();
    const flows = (await this.worker.getFlows()).map((raw) => this.toFlow(raw));
    this.flowsSubject.next(flows);
    return flows;
  }

  async getFlowSummaries(environment?: string): Promise<Flow[]> {
    await this.ensureReady();
    return (await this.worker.getFlowSummaries(environment)).map((raw) => this.toFlowSummary(raw));
  }

  async getFlowsAdmin(): Promise<Flow[]> {
    await this.ensureReady();
    const flows = (await this.worker.getFlowsAdmin()).map((raw) => this.toFlow(raw));
    this.flowsAdminSubject.next(flows);
    return flows;
  }

  getFlows$(): Observable<Flow[]> {
    this.getFlows().catch((error) => console.warn('[StorageWorkerBackendService] getFlows$ refresh failed', error));
    return this.flowsSubject.asObservable();
  }

  getFlowsAdmin$(): Observable<Flow[]> {
    this.getFlowsAdmin().catch((error) => console.warn('[StorageWorkerBackendService] getFlowsAdmin$ refresh failed', error));
    return this.flowsAdminSubject.asObservable();
  }

  async putFlow(flow: Flow, admin = false, options?: StoragePutOptions): Promise<any> {
    await this.ensureReady();
    await this.worker.putFlow(this.toPlainFlow(flow), admin, options);
    this.scheduleFlowRefresh();
    return true;
  }

  async putFlows(flows: Flow[], admin = false, options?: StoragePutOptions): Promise<any> {
    await this.ensureReady();
    await this.worker.putFlows((flows || []).map((flow) => this.toPlainFlow(flow)), admin, options);
    this.scheduleFlowRefresh();
    return true;
  }

  async removeFlows(flows: Flow[], admin = false): Promise<any> {
    await this.ensureReady();
    await this.worker.removeFlows((flows || []).map((flow) => this.toPlainFlow(flow)), admin);
    this.scheduleFlowRefresh();
    return true;
  }

  async markRunScanQueued(environment: string, flowNames: string[], options: RunScanQueueOptions = {}): Promise<any> {
    await this.ensureReady();
    await this.worker.markRunScanQueued(environment, flowNames, options.reason, options.error);
    this.scheduleFlowRefresh();
    return true;
  }

  async clearRunScanQueued(environment: string, flowNames: string[]): Promise<any> {
    await this.ensureReady();
    await this.worker.clearRunScanQueued(environment, flowNames);
    this.scheduleFlowRefresh();
    return true;
  }

  async putRuns(runs: FlowRun[]): Promise<any> {
    await this.ensureReady();
    await this.worker.putRuns((runs || []).map((run) => this.toPlainRun(run)));
    this.scheduleRunsRefresh(null);
    return true;
  }

  getRuns$(environment: string | null = null): Observable<FlowRun[]> {
    this.refreshRunsSubject(environment).catch((error) => {
      console.warn('[StorageWorkerBackendService] getRuns$ refresh failed', error);
    });
    return this.runsSubject.asObservable();
  }
  async getUsers(): Promise<UserItem[]> {
    await this.ensureReady();
    const users = await this.worker.getUsers() as UserItem[];
    this.usersSubject.next(users);
    return users;
  }

  getUsers$(): Observable<UserItem[]> {
    this.getUsers().catch((error) => console.warn('[StorageWorkerBackendService] getUsers$ refresh failed', error));
    return this.usersSubject.asObservable();
  }

  async getUserByEmail(email: string): Promise<UserItem | undefined> {
    await this.ensureReady();
    return this.worker.getUserByEmail(email) as Promise<UserItem | undefined>;
  }

  async putUsers(users: UserItem[]): Promise<any> {
    await this.ensureReady();
    await this.worker.putUsers(users || []);
    this.scheduleUsersRefresh();
    return true;
  }
  async getEnvironment(name: string): Promise<StoredEnvironment | null> {
    await this.ensureReady();
    return this.worker.getEnvironment(name);
  }

  async getEnvironments(): Promise<StoredEnvironment[]> {
    await this.ensureReady();
    return this.worker.getEnvironments();
  }

  async putEnvironment(env: any): Promise<any> {
    await this.ensureReady();
    return this.worker.putEnvironment(env);
  }

  async putEnvironments(envs: any[]): Promise<any> {
    await this.ensureReady();
    return this.worker.putEnvironments(envs);
  }

  async markEnvScanCompleted(name: string, flowCount: number, durationMs: number): Promise<any> {
    await this.ensureReady();
    return this.worker.markEnvScanCompleted(name, flowCount, durationMs);
  }

  async markEnvSourceScanned(name: string, source: EnvScanSource, count?: number, durationMs?: number): Promise<any> {
    await this.ensureReady();
    return this.worker.markEnvSourceScanned(name, source, count, durationMs);
  }

  async getStoreStats(): Promise<StoreStats> {
    await this.ensureReady();
    return this.worker.getStoreStats();
  }

  async nukeStore(): Promise<void> {
    await this.ensureReady();
    await this.worker.nukeStore();
    this.flowsSubject.next([]);
    this.flowsAdminSubject.next([]);
    this.runsSubject.next([]);
    this.usersSubject.next([]);
    this.connectionsSubject.next([]);
  }
  async getAllConfigs(): Promise<{ key: string; value: any }[]> {
    await this.ensureReady();
    return this.worker.getAllConfigs();
  }

  async getAllUserAliases(): Promise<{ email: string; user_key: string }[]> {
    await this.ensureReady();
    return this.worker.getAllUserAliases();
  }

  async getConnections(environment?: string): Promise<Connection[]> {
    await this.ensureReady();
    const connections = (await this.worker.getConnections(environment)).map((raw) => this.toConnection(raw));
    this.connectionsSubject.next(connections);
    return connections;
  }

  getConnections$(): Observable<Connection[]> {
    this.getConnections().catch((error) => console.warn('[StorageWorkerBackendService] getConnections$ refresh failed', error));
    return this.connectionsSubject.asObservable();
  }

  async putConnections(connections: Connection[], environment: string, scope: 'user' | 'admin'): Promise<any> {
    await this.ensureReady();
    await this.worker.putConnections((connections || []).map((connection) => this.toPlainConnection(connection)), environment, scope);
    this.scheduleConnectionsRefresh();
    return true;
  }

  private scheduleFlowRefresh(): void {
    if (this.flowRefreshScheduled) return;
    this.flowRefreshScheduled = true;
    setTimeout(() => {
      this.flowRefreshScheduled = false;
      this.refreshFlowSubjects().catch((error) => {
        console.warn('[StorageWorkerBackendService] flow subject refresh failed', error);
      });
    }, 0);
  }

  private scheduleRunsRefresh(environment: string | null): void {
    if (this.runsRefreshScheduled) return;
    this.runsRefreshScheduled = true;
    setTimeout(() => {
      this.runsRefreshScheduled = false;
      this.refreshRunsSubject(environment).catch((error) => {
        console.warn('[StorageWorkerBackendService] runs subject refresh failed', error);
      });
    }, 0);
  }

  private scheduleUsersRefresh(): void {
    if (this.usersRefreshScheduled) return;
    this.usersRefreshScheduled = true;
    setTimeout(() => {
      this.usersRefreshScheduled = false;
      this.getUsers().catch((error) => {
        console.warn('[StorageWorkerBackendService] users subject refresh failed', error);
      });
    }, 0);
  }

  private scheduleConnectionsRefresh(): void {
    if (this.connectionsRefreshScheduled) return;
    this.connectionsRefreshScheduled = true;
    setTimeout(() => {
      this.connectionsRefreshScheduled = false;
      this.getConnections().catch((error) => {
        console.warn('[StorageWorkerBackendService] connections subject refresh failed', error);
      });
    }, 0);
  }

  private async refreshFlowSubjects(): Promise<void> {
    const [flows, admin] = await Promise.all([
      this.worker.getFlows(),
      this.worker.getFlowsAdmin(),
    ]);
    this.flowsSubject.next(flows.map((raw) => this.toFlow(raw)));
    this.flowsAdminSubject.next(admin.map((raw) => this.toFlow(raw)));
  }

  private toFlow(raw: any): Flow {
    const flow = Object.assign(new Flow(), raw || {});
    flow.runs = flow.runs || [];
    flow.sharingType = flow.sharingType || '';
    flow.sources = normalizeSources(raw?.sources ?? raw?.source);
    if (raw?.modified) flow.modified = new Date(raw.modified);
    if (raw?.scanned) flow.scanned = new Date(raw.scanned);
    return flow;
  }

  private toFlowSummary(raw: any): Flow {
    const flow = Object.assign(new Flow(), raw || {});
    flow.sources = normalizeSources(raw?.sources ?? raw?.source);
    if (raw?.modified) flow.modified = new Date(raw.modified);
    if (raw?.scanned) flow.scanned = new Date(raw.scanned);
    flow.runs = [];
    return flow;
  }

  private toPlainFlow(flow: Flow): any {
    if (!flow) return flow;
    return {
      ...flow,
      modified: flow.modified ? flow.modified.toISOString() : null,
      scanned: flow.scanned ? flow.scanned.toISOString() : null,
      runs: [],
    };
  }

  private async refreshRunsSubject(environment: string | null): Promise<void> {
    await this.ensureReady();
    const runs = (await this.worker.getRuns(environment)).map((raw) => this.toRun(raw));
    this.runsSubject.next(runs);
  }

  private toRun(raw: any): FlowRun {
    const run = Object.assign(new FlowRun(), raw || {});
    if (raw?.startTime) run.startTime = new Date(raw.startTime);
    if (raw?.endTime) run.endTime = new Date(raw.endTime);
    return run;
  }

  private toPlainRun(run: FlowRun): any {
    if (!run) return run;
    return {
      ...run,
      startTime: run.startTime ? run.startTime.toISOString() : null,
      endTime: run.endTime ? run.endTime.toISOString() : null,
    };
  }

  private toConnection(raw: any): Connection {
    const connection = Object.assign(new Connection(), raw || {});
    if (raw?.createdTime) connection.createdTime = new Date(raw.createdTime);
    if (raw?.lastModifiedTime) connection.lastModifiedTime = new Date(raw.lastModifiedTime);
    connection.sources = Array.isArray(raw?.sources) ? raw.sources : [];
    return connection;
  }

  private toPlainConnection(connection: Connection): any {
    if (!connection) return connection;
    return {
      ...connection,
      createdTime: connection.createdTime ? connection.createdTime.toISOString() : null,
      lastModifiedTime: connection.lastModifiedTime ? connection.lastModifiedTime.toISOString() : null,
      metadata: connection.metadata || {},
      raw: null,
    };
  }
}
